import { PROTOCOL_VERSION, type ClientCommandPayload, type ServerMessage, type TerminalSnapshot } from '@head-of-trading/contracts';
import { appStore, saveSession } from '../store/app-store.js';

type SnapshotListener = (snapshot: TerminalSnapshot) => void;
// Le moteur agrège à la seconde et conserve au plus 1 800 secondes. Les deltas
// transportent seulement la queue, mais le client garde toute la fenêtre reçue
// dans le SNAPSHOT initial (M3 complet = 1 141 bougies).
const MAX_CHART_POINTS = 1_800;

/** Merge an overlapping compact STATE_DELTA into the last complete state. */
export function mergeStateDelta(previous: TerminalSnapshot | null, delta: TerminalSnapshot): TerminalSnapshot {
  if (!previous || previous.attemptId !== delta.attemptId) return delta;
  const markets: Record<string, TerminalSnapshot['markets'][string]> = { ...previous.markets };
  for (const [symbol, market] of Object.entries(delta.markets)) {
    const prior = previous.markets[symbol];
    if (!prior) {
      markets[symbol] = market;
      continue;
    }
    const pointsByTime = new Map(prior.chart.map((point) => [point.time, point]));
    for (const point of market.chart) pointsByTime.set(point.time, point);
    const chart = [...pointsByTime.values()]
      .sort((left, right) => left.time - right.time)
      .slice(-MAX_CHART_POINTS);
    markets[symbol] = { ...market, chart };
  }
  return { ...delta, markets };
}

class AttemptSocket {
  private socket: WebSocket | null = null;
  private attemptId: string | null = null;
  private reconnectTimer: number | null = null;
  private reconnectCount = 0;
  private intentionallyClosed = false;
  private terminalListener: SnapshotListener | null = null;
  private eventMessages: TerminalSnapshot['messages'] = [];

  connect(attemptId: string, onSnapshot?: SnapshotListener): void {
    this.close();
    this.attemptId = attemptId;
    this.terminalListener = onSnapshot ?? null;
    this.eventMessages = [];
    this.intentionallyClosed = false;
    this.open();
  }

  private open(): void {
    if (!this.attemptId) return;
    const state = appStore.getState();
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL.replace(/\/$/, '');
    const url = `${protocol}//${location.host}${baseUrl}/ws/attempts/${encodeURIComponent(this.attemptId)}?afterSeq=${state.lastSeq}`;
    appStore.set((current) => current.snapshot ? { snapshot: { ...current.snapshot, connection: this.reconnectCount ? 'RECONNECTING' : 'CONNECTED' } } : {});
    this.socket = new WebSocket(url);
    this.socket.addEventListener('open', () => { this.reconnectCount = 0; });
    this.socket.addEventListener('message', (event) => this.onMessage(event));
    this.socket.addEventListener('close', (event) => this.onClose(event));
    this.socket.addEventListener('error', () => { this.socket?.close(); });
  }

  private onMessage(event: MessageEvent<string>): void {
    let message: ServerMessage;
    try { message = JSON.parse(event.data) as ServerMessage; } catch { return; }
    appStore.set({ lastSeq: Math.max(appStore.getState().lastSeq, message.seq) });
    if (message.type === 'SNAPSHOT' || message.type === 'STATE_DELTA') {
      const previous = appStore.getState();
      const merged = message.type === 'STATE_DELTA' ? mergeStateDelta(previous.snapshot, message.snapshot) : message.snapshot;
      const selectedSymbol = previous.selectedSymbol && merged.markets[previous.selectedSymbol] ? previous.selectedSymbol : merged.selectedSymbol;
      const snapshotIds = new Set(merged.messages.map(({ id }) => id));
      const retainedEvents = this.eventMessages.filter(({ id }) => !snapshotIds.has(id));
      this.eventMessages = retainedEvents;
      const displayed = { ...merged, messages: [...merged.messages, ...retainedEvents], connection: 'CONNECTED' as const };
      appStore.set({ snapshot: displayed, selectedSymbol });
      const account = appStore.getState().account;
      if (account) saveSession(account, displayed.attemptId);
      this.terminalListener?.(displayed);
      return;
    }
    if (message.type === 'MARKET_BATCH') {
      appStore.set((current) => current.snapshot ? {
        snapshot: { ...current.snapshot, markets: { ...current.snapshot.markets, ...message.markets }, simulatedAt: message.simulatedAt, connection: 'CONNECTED' }
      } : {});
      return;
    }
    if (message.type === 'EVENT') {
      if (!this.eventMessages.some(({ id }) => id === message.event.id)) this.eventMessages = [...this.eventMessages, message.event];
      appStore.set((current) => current.snapshot ? {
        snapshot: { ...current.snapshot, messages: current.snapshot.messages.some(({ id }) => id === message.event.id) ? current.snapshot.messages : [...current.snapshot.messages, message.event] }
      } : {});
      return;
    }
    if (message.type === 'ACK' && !message.accepted) appStore.set({ error: message.reason ?? 'Commande refusée.' });
    if (message.type === 'CONTROL_REVOKED') {
      this.intentionallyClosed = true;
      appStore.set({ controlRevoked: true, error: message.reason });
      this.socket?.close();
    }
  }

  private onClose(event?: CloseEvent): void {
    this.socket = null;
    if (event?.code === 4001) {
      this.intentionallyClosed = true;
      appStore.set({ controlRevoked: true, error: 'Le contrôle a été repris dans une autre fenêtre.' });
    }
    if (this.intentionallyClosed || !this.attemptId) return;
    appStore.set((current) => current.snapshot ? { snapshot: { ...current.snapshot, connection: 'RECONNECTING' } } : {});
    const delay = Math.min(5_000, 400 * (2 ** this.reconnectCount++));
    this.reconnectTimer = window.setTimeout(() => this.open(), delay);
  }

  send(payload: ClientCommandPayload): string | null {
    const attemptId = this.attemptId;
    if (!attemptId || this.socket?.readyState !== WebSocket.OPEN) {
      appStore.set({ error: 'Connexion au moteur indisponible. La commande n’a pas été envoyée.' });
      return null;
    }
    const commandId = crypto.randomUUID();
    this.socket.send(JSON.stringify({ protocolVersion: PROTOCOL_VERSION, commandId, attemptId, clientSentAt: new Date().toISOString(), payload }));
    appStore.set({ error: null });
    return commandId;
  }

  close(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
    this.attemptId = null;
    this.eventMessages = [];
  }
}

export const attemptSocket = new AttemptSocket();
